import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import chroma from 'chroma-js';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import GeneralText from '../../stories/general-components/GeneralText';
import { useReactToPrint } from 'react-to-print';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Paper } from '@mui/material';
import nodeAxiosFirebase from '../../utils/nodeAxiosFirebase';
import FieldComponent from '../../components/@generalComponents/FieldComponent';
import Button from '../../stories/general-components/Button';

const RetrievePublicElement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isDarkMode = theme.palette.mode === 'dark';

  const { structureId, moduleName, businessId } = useParams();
  const softwareVersion = process.env.REACT_APP_VERSION;

  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [mainColor, setMainColor] = useState('#200EF0');
  const [secColor, setSecColor] = useState('#200EF090');
  const [businessData, setBusinessData] = useState({});
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const shadowColor = chroma(mainColor).darken(1.25).alpha(0.1).css();



  useEffect(() => {
    if (businessId && businessId !== null && structureId && structureId !== null) {
      const fetchData = async () => {
        try {

          const response = await nodeAxiosFirebase({
            t,
            noAuth: true,
            method: 'GET',
            url: `public/business?businessId=${businessId}&structureId=${structureId}`,
          });

          if (response?.data?.business?.mainColor !== undefined && response?.data?.business?.mainColor !== null) {
            setMainColor(response?.data?.business?.mainColor);
            setSecColor(response?.data?.business?.secColor);
          }

          if (response?.data?.fields !== undefined && response?.data?.fields !== null) {
            setFields(response?.data?.fields);
          }

          if (response?.data?.business !== undefined && response?.data?.business !== null) {
            setBusinessData(response?.data?.business);
          }

        } catch (error) {
          console.error(error);
        }
      };
      fetchData();
    }
  }, [businessId, structureId]);


  const retrieveData = async () => {
    try {
      setLoading(true);
      const response = await nodeAxiosFirebase({
        t,
        noAuth: true,
        method: 'GET',
        url: `public/retrieve?businessId=${businessId}&structureId=${structureId}&moduleName=${moduleName}&param1=${formData?.[fields?.fields?.[0]?.value]}&param2=${formData?.[fields?.fields?.[1]?.value]}`,
      });

      if (response?.elementId) {
        navigate(`/doc/${businessId}/${moduleName}/${structureId}/${response?.elementId}?accessCode=${response?.accessCode}&shared=true`);
      }

      setLoading(false);

    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div
      style={{
        backgroundColor: isDarkMode ? '#000000' : '#FFFFFF',
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100vh',
        width: '100vw',
        overflow: isMobile ? 'auto' : 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >


      {/* Background gradient effect */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at center, ${mainColor}16 0%, transparent 70%)`,
          filter: 'blur(60px)',
          zIndex: 0,
        }}
      />


      {/* Main content container */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: '480px',
          padding: isMobile ? '20px' : '40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Glassmorphism card */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '30px',
            width: '100%',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: `0 8px 32px 0 ${shadowColor}`,
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <div
              style={{
                borderRadius: '8px',
                padding: '15px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '25px',
              }}

            >
              <img
                src={businessData?.logo}
                height="30px"
                alt={businessData?.name}
              />
            </div>
            <GeneralText
              text={businessData?.name}
              fontSize="24px"
              size="bold"
              primary={true}
              classNameComponent="mt-3"
            />
            <GeneralText
              text={t('findAnElement')}
              fontSize="14px"
              size="regular"
              primary={true}
              classNameComponent="mt-1"
            />
          </div>
          {fields?.fields?.[0] && (
            <FieldComponent
              value={formData?.[fields?.fields?.[0]?.value]}
              field={{
                ...fields?.fields?.[0],
                name: fields?.fields?.[0]?.['name_' + language]
              }}
              key={fields?.fields?.[0]?.value}
              onChange={(key, value) => setFormData({ ...formData, [fields?.fields?.[0]?.value]: value })}
            />)}
          {fields?.fields?.[1] && (
            <FieldComponent
              value={formData?.[fields?.fields?.[1]?.value]}
              field={{
                ...fields?.fields?.[1],
                name: fields?.fields?.[1]?.['name_' + language]
              }}
              key={fields?.fields?.[1]?.value}
              onChange={(key, value) => setFormData({ ...formData, [fields?.fields?.[1]?.value]: value })}
            />
          )}

          <Button
            label={t('search')}
            onClick={retrieveData}
            endIcon={'SearchOutlined'}
            fullWidth
            color={mainColor}
            disableElevation
            disabled={!formData?.[fields?.fields?.[0]?.value] || !formData?.[fields?.fields?.[1]?.value]}
            buttonSx={{
              height: '48px',
              marginTop: '20px',
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '12px',
              backgroundColor: mainColor,
              '&:hover': {
                backgroundColor: mainColor,
              },
            }}
            currentLoading={loading === true}
          />

        </Paper>

        {/* Footer */}
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            padding: '20px',
            backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 3,
          }}
        >
          <div style={{ display: 'flex', gap: '20px' }}>
            <GeneralText
              text={t('backwebsite')}
              fontSize="0.8rem"
              size="regular"
              primary={true}
              classNameComponent="hover"
              onClick={() => navigate('/')}
            />
            <GeneralText
              text={t('privacyPolicy')}
              fontSize="0.8rem"
              size="regular"
              primary={true}
              classNameComponent="hover"
              onClick={() => navigate('/informations/mentions-legales')}
            />
            <GeneralText
              text={'Version ' + softwareVersion || ''}
              fontSize="0.8rem"
              size="regular"
              primary={true}
            />
          </div>
          <GeneralText
            text={'Intelligence Node Canada'}
            fontSize="0.8rem"
            size="regular"
            primary={true}
          />
        </div>
      </div>
    </div>
  );
};

export default RetrievePublicElement;
